<script setup lang="ts">
import type { CVData } from '~/types/cv'
import type { InterviewPrepPreview } from '~/utils/interviewPrep'

/**
 * The interview prep sold on its own, without an account
 * (/api/guest-export/interview-prep).
 *
 * Two ways in:
 * - `sourceToken`: on a tailored PDF that was just bought. The posting is
 *   already known, so the card writes the prep as soon as it is shown.
 * - `cvData`: on any CV's result page, a free Basic download included (and
 *   where the follow-up e-mail sends people, ?focus=interview). The visitor
 *   pastes the posting they are invited for first. Right under a tailoring
 *   (`knownPosting`) they are asked instead: the same posting writes the prep
 *   at once, another one opens the field for it.
 *
 * Either way the card proves itself instead of describing itself: the answer
 * to "Tell me about yourself" and one question in full, about half of the
 * other questions with their answers locked and the rest only counted. It
 * also opens in a wide dialog (the whole screen on a phone), easier to read
 * than the column, with the prep's own price and the pack under it. The
 * payment is a guest checkout on the prep's own token; once it lands, the
 * prep opens on its page.
 *
 * It stays out of the way when it has nothing to show: not on sale, a PDF
 * parked before the posting was kept with it, or a visitor whose plan
 * already includes the prep in the editor.
 */
const props = withDefaults(defineProps<{
  /** The paid `tailored-pdf` guest export. */
  sourceToken?: string | null
  /** The CV on the page, when there is no tailored PDF to start from. */
  cvData?: CVData | null
  /** Its `guest_cv_generations` id and the layout on screen, kept with the purchase. */
  generationId?: string | null
  template?: string
  /** The posting the CV was just tailored to: offered as "the same one" before the field. */
  knownPosting?: string | null
  /** Inside a card that has its own frame. */
  bare?: boolean
  /** Over the title instead of the general one, e.g. which step of the result this is. */
  eyebrow?: string
  /**
   * Already paid for with the tailored PDF (the application kit): the prep is
   * written as soon as the card is shown and opens without a price, whatever
   * the visitor's plan and whether the prep is on sale on its own.
   */
  included?: boolean
}>(), {
  sourceToken: null,
  cvData: null,
  generationId: null,
  template: undefined,
  knownPosting: null,
  bare: false,
  eyebrow: undefined,
  included: false,
})

const emit = defineEmits<{
  /** Unlock with the Job Search Pack: the host opens its checkout. */
  'buy-pack': []
}>()

const MAX_POSTING = 8000

interface OfferResponse {
  token: string
  paid: boolean
  preview: InterviewPrepPreview | null
}

const { t, locale } = useI18n()
const { getPlan, interviewPrepPrice } = usePremiumPlans()
const { isPremium, isChecked: isPlanKnown } = usePremiumStatus()
const journey = useJourney()

const offer = ref<OfferResponse | null>(null)
const state = ref<'idle' | 'loading' | 'ready' | 'error' | 'hidden' | 'limit'>('idle')
/** No tailored PDF: the visitor pastes the posting first. */
const askPosting = computed(() => !props.sourceToken)
const posting = ref('')
/**
 * The card starts small: its title, a line and a button. The field opens on
 * the click (or on "another posting"), so the column under a CV stays short.
 */
const opened = ref(false)
/** A posting is known: "same or another?" comes before the field, which opens only for another. */
const choosing = ref(true)
const askSame = computed(() => askPosting.value && choosing.value && !!props.knownPosting?.trim())

/** The posting the CV was tailored to, written at once. */
function useSame() {
  posting.value = props.knownPosting?.trim() ?? ''
  choosing.value = false
  load()
}

function openField() {
  opened.value = true
  nextTick(() => document.getElementById('interview-prep-offer-posting')?.focus())
}

function useOther() {
  posting.value = ''
  choosing.value = false
  openField()
}

// A new tailoring, a new question.
watch(() => props.knownPosting, () => {
  if (state.value !== 'idle') return
  choosing.value = true
  posting.value = ''
})

const onSale = computed(() => !!getPlan('interview-prep'))
/** The pack and the yearly plan have the prep in the editor: nothing to sell them. */
const wanted = computed(() => (props.included && !!props.sourceToken) || (onSale.value && isPlanKnown.value && !isPremium.value))
const prepUrl = computed(() => (offer.value ? `/interview-prep/${offer.value.token}` : null))

async function load() {
  if (state.value === 'loading') return
  if (askPosting.value && (!props.cvData || !posting.value.trim())) return
  state.value = 'loading'
  try {
    offer.value = await $fetch<OfferResponse>('/api/guest-export/interview-prep', {
      method: 'POST',
      body: askPosting.value
        ? {
            cvData: props.cvData,
            jobDescription: posting.value.trim().slice(0, MAX_POSTING),
            template: props.template,
            locale: locale.value,
            generationId: props.generationId ?? undefined,
            visitorId: journey.visitorId() ?? undefined,
          }
        : { sourceToken: props.sourceToken, locale: locale.value },
    })
    state.value = 'ready'
    if (offer.value?.preview) modalOpen.value = true
    journey.track('interview_prep_preview', { generation_id: props.generationId ?? null, from: askPosting.value ? 'posting' : 'tailored' })
  } catch (err) {
    // Not on sale, no posting kept with this PDF, or an unknown token: no card at all.
    const status = (err as { statusCode?: number }).statusCode
    state.value = status === 429 ? 'limit' : status === 404 || status === 409 ? 'hidden' : 'error'
  }
}

// After a tailored PDF the posting is known: the prep is written the first
// time a buyer who could want it sees the card.
watch(wanted, (value) => {
  if (value && state.value === 'idle' && !askPosting.value) load()
}, { immediate: true })

/** The preview, read in a wide dialog; the card keeps it too. */
const modalOpen = ref(false)
/** The dialog stepped aside for the checkout and comes back when it closes unpaid. */
const reopenModal = ref(false)

/* The checkout of this one prep; once paid, the prep opens on its page. */
const { checkoutUrl, isStarting, error: checkoutError, buy } = useInterviewPrepCheckout(
  () => offer.value?.token,
  { onPaid: async (token) => {
    reopenModal.value = false
    await navigateTo(`/interview-prep/${token}`)
  } },
)

/** From the dialog: the checkout cannot show over the <dialog> (top layer), so it closes first. */
async function buyFromModal() {
  reopenModal.value = true
  modalOpen.value = false
  await buy()
  if (!checkoutUrl.value && reopenModal.value) {
    reopenModal.value = false
    modalOpen.value = true
  }
}

watch(checkoutUrl, (url) => {
  if (url || !reopenModal.value) return
  reopenModal.value = false
  modalOpen.value = true
})

function buyPackFromModal() {
  modalOpen.value = false
  buyPack()
}

/**
 * The pack instead: the host runs its checkout (it knows the CV), and the
 * editor shows this same prep whole once the pack is on the account.
 */
const { setPendingInterview } = usePendingPurchase()
function buyPack() {
  if (offer.value?.token) setPendingInterview(offer.value.token)
  journey.track('interview_prep_preview', { generation_id: props.generationId ?? null, from: 'pack' })
  emit('buy-pack')
}
</script>

<template>
  <div v-if="wanted && state !== 'hidden' && (state !== 'idle' || askPosting)" :class="bare ? '' : 'card'">
    <div class="flex items-start justify-between gap-4">
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide" :class="eyebrow ? 'text-accent-strong dark:text-accent-soft' : 'text-muted dark:text-gray-400'">
          {{ eyebrow || t('interviewPrep.offer.eyebrow') }}
        </div>
        <div class="mt-1 font-title text-xl font-bold text-ink dark:text-white">{{ t('interviewPrep.offer.title') }}</div>
      </div>
      <i class="bi bi-chat-quote shrink-0 text-4xl leading-none text-lilac" aria-hidden="true" />
    </div>
    <p class="mt-2 !text-base">{{ t(included ? 'interviewPrep.offer.includedText' : 'interviewPrep.offer.text') }}</p>

    <!-- Just tailored to a posting: the same one in one tap, or another -->
    <template v-if="askSame && state === 'idle'">
      <p class="mt-5 font-title !text-base font-semibold text-ink dark:text-white">{{ t('interviewPrep.offer.sameQuestion') }}</p>
      <div class="mt-3 grid gap-2 sm:grid-cols-2">
        <SofaxBtn variant="dark" block :label="t('interviewPrep.offer.sameCta')" @click="useSame" />
        <SofaxBtn variant="outline" block :label="t('interviewPrep.offer.otherCta')" @click="useOther" />
      </div>
      <p class="mt-3 text-center !text-sm text-muted dark:text-gray-400">{{ t('interviewPrep.offer.previewNote', { price: interviewPrepPrice }) }}</p>
    </template>

    <!-- Closed until asked for -->
    <template v-else-if="askPosting && state === 'idle' && !opened">
      <SofaxBtn
        variant="dark"
        block
        class="mt-5"
        :label="t('interviewPrep.offer.openCta')"
        @click="openField"
      />
    </template>

    <!-- No tailored PDF behind it: the posting comes first -->
    <template v-else-if="askPosting && (state === 'idle' || state === 'limit' || state === 'error')">
      <SofaxTextarea
        id="interview-prep-offer-posting"
        v-model="posting"
        class="mt-5"
        :label="t('interviewPrep.list.posting')"
        :hint="t('interviewPrep.list.postingHint')"
        :rows="6"
        :maxlength="MAX_POSTING"
      />
      <SofaxBtn
        variant="dark"
        block
        class="mt-4"
        :disabled="!posting.trim()"
        :label="t('interviewPrep.offer.previewCta')"
        @click="load"
      />
      <p class="mt-3 text-center !text-sm text-muted dark:text-gray-400">{{ t('interviewPrep.offer.previewNote', { price: interviewPrepPrice }) }}</p>
      <SofaxNotice v-if="state === 'limit'" type="info" class="mt-4">{{ t('interviewPrep.offer.limit') }}</SofaxNotice>
      <SofaxNotice v-else-if="state === 'error'" type="error" class="mt-4">{{ t('interviewPrep.offer.error') }}</SofaxNotice>
    </template>

    <!-- Writing it takes a little while: say so, the card fills in by itself -->
    <div v-if="state === 'loading'" class="mt-5 flex items-center gap-3 text-muted dark:text-gray-400">
      <span class="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      <span class="!text-sm">{{ t('interviewPrep.offer.loading') }}</span>
    </div>

    <SofaxNotice v-else-if="state === 'error' && !askPosting" type="error" class="mt-5">
      {{ t('interviewPrep.offer.error') }}
      <button type="button" class="link ml-1" @click="load">{{ t('interviewPrep.offer.retry') }}</button>
    </SofaxNotice>

    <template v-else-if="offer?.paid && prepUrl">
      <SofaxBtn variant="accent" block class="mt-5" :to="prepUrl" :label="t('interviewPrep.offer.open')" />
    </template>

    <template v-else-if="offer?.preview">
      <button type="button" class="action-pill mt-5 h-8 !text-xs" @click="modalOpen = true">
        <i class="bi bi-arrows-angle-expand" aria-hidden="true" />
        {{ t('interviewPrep.editor.show') }}
      </button>
      <!-- Every locked part opens the choice right under it -->
      <div class="mt-3 rounded-[0.625rem] bg-surface p-4 dark:bg-gray-900">
        <InterviewPrepView
          :preview="offer.preview"
          :unlock="{ canBuySingle: true, busy: isStarting }"
          tease-half
          @buy-single="buy"
          @buy-pack="buyPack"
        />
      </div>

      <div class="mt-5">
        <div class="mb-2 font-title text-base font-semibold text-ink dark:text-white">{{ t('interviewPrep.unlock.allTitle') }}</div>
        <InterviewPrepUnlockChoice can-buy-single :busy="isStarting" @buy-single="buy" @buy-pack="buyPack" />
      </div>
      <p class="mt-3 text-center !text-sm text-muted dark:text-gray-400">{{ t('interviewPrep.offer.note') }}</p>
      <SofaxNotice v-if="checkoutError" type="error" class="mt-4">{{ t('premium.checkoutError') }}</SofaxNotice>
    </template>

    <!-- The same preview, wide; the whole screen on a phone -->
    <CommonDialog
      v-if="offer?.preview && !offer.paid"
      v-model="modalOpen"
      :title="t('interviewPrep.view.title')"
      size="4xl"
      fullscreen-mobile
    >
      <InterviewPrepView
        :preview="offer.preview"
        :unlock="{ canBuySingle: true, busy: isStarting }"
        tease-half
        @buy-single="buyFromModal"
        @buy-pack="buyPackFromModal"
      />
      <!-- Pinned under the scrolling preview: the green one buys this prep, the dark one everything -->
      <template #footer>
        <InterviewPrepBuyBar can-buy-single :busy="isStarting" :error="!!checkoutError" @buy-single="buyFromModal" @buy-pack="buyPackFromModal" />
      </template>
    </CommonDialog>

    <InterviewPrepCheckoutFrame v-model="checkoutUrl" />
  </div>
</template>
